import { css, keyframes } from '@emotion/react';
import { theme } from '../../../design-system/theme/theme';

/** 진행 중 셀 위를 좌→우로 지나가는 빛. */
const sweep = keyframes`
  0% { transform: translateX(-150%); }
  100% { transform: translateX(150%); }
`;

/** 가로 스크롤 래퍼 — 스테이지가 많아지면 표가 넘칠 수 있어 감싼다. */
export const scrollStyle = css`
  overflow-x: auto;
`;

/** 스테이지 매트릭스. 첫 열은 빌드 정보, 이후 열은 스테이지별 셀. */
export const gridStyle = css`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  min-width: 720px;
  font-size: ${theme.fontSize.sm}px;
  table-layout: fixed;
`;

/** 좌상단 코너 — 평균 소요 안내가 들어가는 빌드 정보 열의 헤더. */
export const cornerStyle = css`
  width: 200px;
  padding: ${theme.space.sm}px ${theme.space.md}px;
  text-align: right;
  color: ${theme.color.textMuted};
  vertical-align: bottom;
  border-bottom: 1px solid ${theme.color.border};
`;

export const cornerMutedStyle = css`
  display: block;
  color: ${theme.color.textMuted};
`;

/** 스테이지 헤더 셀 — 이름 + 평균 소요. */
export const headStyle = css`
  padding: ${theme.space.sm}px ${theme.space.md}px;
  text-align: center;
  border-bottom: 1px solid ${theme.color.border};
  border-left: 1px solid ${theme.color.border};
  background: ${theme.color.bg};
`;

export const headNameStyle = css`
  font-weight: 600;
  color: ${theme.color.text};
`;

export const headAvgStyle = css`
  margin-top: ${theme.space.xs}px;
  color: ${theme.color.textMuted};
`;

/** 클릭 가능한 행 — 상세로 이동. hover 시 살짝 강조. */
export const rowClickableStyle = css`
  cursor: pointer;

  &:hover td {
    filter: brightness(0.97);
  }
`;

/** 빌드 정보 셀(각 행 왼쪽). */
export const runInfoStyle = (failed: boolean) => css`
  padding: ${theme.space.sm}px ${theme.space.md}px;
  border-bottom: 1px solid ${theme.color.border};
  background: ${failed ? 'rgba(224, 57, 62, 0.06)' : theme.color.surface};
  /* {module} (collector-type) 등 긴 라벨이 고정폭(200px) 안에서 줄바꿈되게. */
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

/** 빌드 번호 배지. */
export const buildBadgeStyle = (failed: boolean) => css`
  display: inline-block;
  padding: 1px ${theme.space.sm}px;
  border-radius: ${theme.radius.pill}px;
  font-weight: 600;
  color: ${theme.color.onPrimary};
  background: ${failed ? theme.color.danger : theme.color.textMuted};
`;

export const runDateStyle = css`
  margin-top: ${theme.space.xs}px;
  color: ${theme.color.text};
`;

export const runChangesStyle = css`
  color: ${theme.color.textMuted};
`;

/** 스테이지 셀 배경 — 상태별. */
const cellBg: Record<string, string> = {
  success: 'rgba(2, 154, 128, 0.10)',
  failed: 'rgba(224, 57, 62, 0.12)',
  running: 'rgba(30, 136, 229, 0.20)',
  aborted: 'rgba(139, 147, 161, 0.10)',
  skipped: theme.color.bg,
  empty: theme.color.bg,
};

const cellText: Record<string, string> = {
  success: theme.color.success,
  failed: theme.color.danger,
  running: theme.color.running,
  aborted: theme.color.textMuted,
  skipped: theme.color.textMuted,
  empty: theme.color.textMuted,
};

export const cellStyle = (status: string) => css`
  padding: ${theme.space.md}px ${theme.space.sm}px;
  text-align: center;
  vertical-align: middle;
  border-bottom: 1px solid ${theme.color.border};
  border-left: 1px solid ${theme.color.border};
  background: ${cellBg[status] ?? theme.color.bg};
  color: ${cellText[status] ?? theme.color.textMuted};
  /* detail/note 등 긴 문구가 셀을 넘치지 않고 줄바꿈되게. */
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

/**
 * 진행 중(running) 셀 강조 — 좌→우로 빛이 반복해서 지나간다.
 * cellStyle과 함께 배열로 합쳐 사용.
 */
export const runningCellStyle = css`
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 300%;
    height: 100%;
    background: linear-gradient(
      110deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.25) 35%,
      rgba(255, 255, 255, 0.7) 50%,
      rgba(255, 255, 255, 0.25) 65%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${sweep} 10s linear infinite;
  }

  /* 접근성: 모션 최소화 설정 시 애니메이션 정지. */
  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

/** 실패 빌드가 한 스테이지를 크게 차지할 때(체크아웃에서 실패 등)의 강조 소요. */
export const cellDurationStyle = css`
  font-size: ${theme.fontSize.md}px;
`;

/** 셀 하단 부가 문구 — "(paused for 1h 24min)", "aborted" 등. */
export const cellNoteStyle = css`
  display: block;
  margin-top: ${theme.space.xs}px;
  font-size: 11px;
  color: ${theme.color.textMuted};
`;
